import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle, CreditCard, Loader2 } from 'lucide-react';
import { loadInvoiceSettlementByPayToken } from '@/hooks/loadInvoiceSettlement';
import { payReturnStorageKey, useCreatePayCheckout } from '@/hooks/useClinicPay';
import { supabase } from '@/lib/supabase';
import {
  clearPortalPaymentPendingIfToken,
  invalidatePortalPaymentQueries,
  isSafePortalPayReturnPath,
  markPortalPaymentPending,
  resolveSafePayReturnPath,
  waitForBackendPaymentSettlement,
} from '@/lib/portalPaymentSettlement';
import { Button } from '@/components/ui/Button';
import { PAGE_TITLE } from '@/components/ui/pageChrome';

/** Same-origin return paths only (blocks open redirects). */
function safePayReturnPath(raw: string | null, payToken: string | undefined): string | null {
  const storedByToken = payToken ? sessionStorage.getItem(payReturnStorageKey(payToken)) : null;
  return resolveSafePayReturnPath(raw, storedByToken);
}

export function PayInvoice() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const createCheckout = useCreatePayCheckout();

  const paymentStatus = searchParams.get('status');
  const isSucceeded = paymentStatus === 'succeeded';
  const isCancelled = paymentStatus === 'cancelled';
  const returnTo = safePayReturnPath(searchParams.get('return_to'), token);
  const isPortalReturn = Boolean(returnTo && isSafePortalPayReturnPath(returnTo));

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoLoading, setInfoLoading] = useState(true);
  const [tokenInvalid, setTokenInvalid] = useState(false);

  const [syncError, setSyncError] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [settlementReady, setSettlementReady] = useState(false);

  useEffect(() => {
    if (!token || isCancelled) {
      if (isCancelled) clearPortalPaymentPendingIfToken(token);
      setInfoLoading(false);
      return;
    }
    if (token.length < 8) {
      setTokenInvalid(true);
      setInfoLoading(false);
      return;
    }

    let cancelled = false;
    async function syncPaymentStatus() {
      try {
        const { data, error: invokeError } = await supabase.functions.invoke('pay-checkout-status', {
          body: { pay_token: token },
        });
        if (cancelled) return;

        if (invokeError) {
          console.error('pay checkout sync failed', invokeError);
          if (isSucceeded) setSyncError(true);
          return;
        }

        const result = data as { ok?: boolean; data?: { status?: string } } | null;
        if (result?.ok && result.data?.status === 'succeeded') {
          setPaymentComplete(true);
        } else if (isSucceeded) {
          setSyncError(true);
        }
      } catch (err) {
        console.error('pay checkout sync failed', err);
        if (!cancelled && isSucceeded) setSyncError(true);
      } finally {
        if (!cancelled) setInfoLoading(false);
      }
    }

    void syncPaymentStatus();
    return () => {
      cancelled = true;
    };
  }, [token, isSucceeded, isCancelled]);

  async function handlePay() {
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await createCheckout.mutateAsync({ pay_token: token });
      window.location.href = result.checkout_url;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('expired') || msg.includes('gone')) {
        setError(t('payments.expired'));
      } else if (msg.includes('already been paid') || msg.includes('already paid')) {
        setError(t('payments.already_paid'));
      } else {
        setError(t('payments.pay_error'));
      }
      setIsLoading(false);
    }
  }

  const showThanks = isSucceeded || paymentComplete;

  useEffect(() => {
    if (isCancelled) {
      setSettlementReady(true);
      return;
    }

    const providerSucceeded = isSucceeded || paymentComplete;
    if (!providerSucceeded) return;

    if (!isPortalReturn || !token) {
      setSettlementReady(true);
      return;
    }

    const payToken = token;
    let cancelled = false;
    async function waitForClinioPaid() {
      markPortalPaymentPending({ payToken });
      const settled = await waitForBackendPaymentSettlement({
        payToken,
        loadInvoice: loadInvoiceSettlementByPayToken,
      });
      if (cancelled) return;
      if (settled?.status === 'paid') {
        markPortalPaymentPending({
          payToken,
          appointmentId: settled.appointment_id,
        });
      }
      await invalidatePortalPaymentQueries(qc);
      if (!cancelled) setSettlementReady(true);
    }

    void waitForClinioPaid();
    return () => {
      cancelled = true;
    };
  }, [token, isCancelled, isSucceeded, paymentComplete, isPortalReturn, qc]);

  useEffect(() => {
    if (!showThanks || !returnTo) return;
    if (isPortalReturn && !settlementReady) return;
    const id = window.setTimeout(() => {
      navigate(returnTo, { replace: true });
    }, isPortalReturn ? 400 : 1200);
    return () => window.clearTimeout(id);
  }, [showThanks, returnTo, navigate, isPortalReturn, settlementReady]);

  if (infoLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page-light dark:bg-page-dark">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (showThanks) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-page-light px-4 dark:bg-page-dark">
        <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-card-dark">
          <CheckCircle className="mx-auto mb-4 h-14 w-14 text-emerald-500" />
          <h1 className={PAGE_TITLE}>
            {t('payments.thanks')}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {returnTo
              ? t(isPortalReturn ? 'payments.thanks_returning_portal' : 'payments.thanks_returning')
              : t('payments.thanks_subtitle')}
          </p>
          {syncError && (
            <p className="mt-3 text-xs text-yellow-700 dark:text-yellow-400">
              {t('payments.sync_delayed')}
            </p>
          )}
          {returnTo && (
            <Button
              className="mt-6"
              size="full"
              onClick={() => navigate(returnTo, { replace: true })}
            >
              {t(isPortalReturn ? 'payments.back_to_invoices' : 'payments.back_to_invoice')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (tokenInvalid || !token) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-page-light px-4 dark:bg-page-dark">
        <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-card-dark">
          <p className="text-sm text-red-600 dark:text-red-400">{t('payments.expired')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-page-light px-4 dark:bg-page-dark">
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-card-dark">
        <div className="rounded-t-xl bg-indigo-600 px-6 py-4">
          <span className="text-lg font-bold text-white">CLINIO</span>
        </div>

        <div className="p-8">
          <h1 className="mb-1 text-xl font-semibold text-gray-900 dark:text-white">
            {t('payments.public_pay_title')}
          </h1>
          <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
            {t('payments.public_pay_subtitle')}
          </p>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
              {error}
            </div>
          )}

          {isCancelled && (
            <div className="mb-4 rounded-md bg-yellow-50 px-4 py-3 text-sm text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
              {t('payments.payment_cancelled')}
            </div>
          )}

          {returnTo && (
            <Button
              type="button"
              variant="link"
              className="mb-4"
              onClick={() => navigate(returnTo, { replace: true })}
            >
              {t(isPortalReturn ? 'payments.back_to_invoices' : 'payments.back_to_invoice')}
            </Button>
          )}

          <Button size="full" onClick={handlePay} disabled={isLoading || createCheckout.isPending}>
            {isLoading || createCheckout.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              <>
                <CreditCard className="mr-2 h-4 w-4" />
                {t('payments.pay_now')}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
